import { Spacer, Spinner, Text } from "@hackthenorth/north";
import React, { useMemo } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import CatchUpQuestCard from "src/views/goose-games/components/cards/CatchUpQuestCard";
import { useMyGooseGameDashboardCatchUpQuery } from "src/views/goose-games/graphql/hacker/myGooseGameDashboardCatchUp.generated";
import {
  GooseGameDashboardCatchUpSummary,
  GooseToQuestTypeMap,
  Quest,
} from "src/views/goose-games/types";

import FooterActions from "../../../../footerActions";

interface WelcomeBackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const mapSummaryQuestToQuest = (
  summary: GooseGameDashboardCatchUpSummary
): Quest => ({
  ...summary.quest,
  type: GooseToQuestTypeMap[summary.quest.type],
  description: summary.quest.description ?? undefined,
  quest_event_activity_category:
    summary.quest.quest_event_activity_category ?? undefined,
  question_string: summary.quest.question_string ?? undefined,
  question_answer: undefined,
  flash: false, //not used in summary
  hidden: false, //not used in summary
});

const getRecentLogCount = (summary: GooseGameDashboardCatchUpSummary) => {
  if (!summary.quest.points_rewarded) {
    return 0;
  }

  return summary.pointsEarned / summary.quest.points_rewarded;
};

const WelcomeBackModal: React.FC<WelcomeBackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const skipQuery = !isOpen;

  const { data: catchUpData, loading } = useMyGooseGameDashboardCatchUpQuery({
    skip: skipQuery,
  });

  const { updatedQuestSummaries, pointsEarned } = useMemo(() => {
    const catchUp = catchUpData?.myGooseGameDashboardCatchUp;

    if (!catchUp) {
      return {
        updatedQuestSummaries: [],
        pointsEarned: 0,
      };
    }

    const updatedQuestSummaries = [
      ...catchUp.completions,
      ...catchUp.progress,
    ].sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
    );

    return {
      updatedQuestSummaries,
      pointsEarned: catchUp.totalPointsEarned,
    };
  }, [catchUpData]);

  const overlayStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "72px",
    paddingBottom: "72px",
    overflowY: "auto" as const,
  };

  return (
    <WelcomeBackModalShell
      isOpen={isOpen}
      overlayStyle={overlayStyle}
      width="none"
    >
      <Text mods="heading h2">
        <span role="img" aria-label="party popper">
          🎉
        </span>{" "}
        Welcome back to Goose Games!
      </Text>
      <Spacer height={16} />
      <EarnedRow>
        <Text mods="grey">While you were away, you earned:</Text>
        <BalanceChip isComplete={false} points={pointsEarned} />
      </EarnedRow>
      <Spacer height={24} />
      <Text mods="heading h3 body grey">Quests Updated</Text>
      {loading ? (
        <Spinner />
      ) : updatedQuestSummaries.length === 0 ? (
        <Text mods="grey">No quests completed while you were away.</Text>
      ) : (
        <HistoryList>
          {updatedQuestSummaries.map((summary) => (
            <CatchUpQuestCard
              key={summary.quest.id}
              quest={mapSummaryQuestToQuest(summary)}
              progress={summary.completionCount}
              pointsIncreased={getRecentLogCount(summary)}
            />
          ))}
        </HistoryList>
      )}
      <BottomActionContainer>
        <FooterActions onConfirm={onClose} confirmText="Keep gaming!" />
      </BottomActionContainer>
    </WelcomeBackModalShell>
  );
};

export default WelcomeBackModal;

const WelcomeBackModalShell = styled(Modal)`
  max-width: unset;
`;

const EarnedRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const BottomActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
`;
